import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

export const fetchShops = createAsyncThunk("shops/fetchShops", async (params = {}) => {
  const { page = 1, per_page = 10 } = params;
  const res = await http.get(`/dashboard/businesses?page=${page}&per_page=${per_page}`);
  return res;
});

export const createShop = createAsyncThunk(
  "shops/createShop",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/businesses", payload);
      return res;
    } catch (err) {
      console.log('Full error:', err);
      console.log('Error response:', err.response);
      console.log('Error response data:', err.response?.data);
      
      // The backend returns: { success: false, message: "...", data: { field: ["error"] } }
      const errorResponse = err.response?.data || {};
      return rejectWithValue({
        message: errorResponse.message || err.message || "Failed to create shop",
        data: errorResponse.data || null,
        success: errorResponse.success
      });
    }
  }
);

export const updateShopStatus = createAsyncThunk(
  "shops/updateShopStatus",
  async (payload, { rejectWithValue }) => {
    console.log(payload)
    try {
      const res = await http.patch(`/dashboard/businesses/${payload.uuid}/toggle-status`, payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update shop status"
      );
    }
  }
);

export const fetchShopById = createAsyncThunk(
  "shops/fetchShopById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/businesses/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch shop details"
      );
    }
  }
);

export const updateShop = createAsyncThunk(
  "shops/updateShop",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.post(`/dashboard/businesses/${uuid}`, data);
      return res;
    } catch (err) {
      console.log('Update shop error:', err);
      console.log('Error response data:', err.response?.data);
      
      // The backend returns: { success: false, message: "...", data: { field: ["error"] } }
      const errorResponse = err.response?.data || {};
      return rejectWithValue({
        message: errorResponse.message || err.message || "Failed to update shop",
        data: errorResponse.data || null,
        success: errorResponse.success
      });
    }
  }
);

// Update shop contact information
export const updateShopContact = createAsyncThunk(
  "shops/updateShopContact",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      // Format data to match backend expectations
      const formattedData = {
        uuid: uuid,
      };

      // Add optional fields only if they exist
      if (data.contact_numbers && data.contact_numbers.length > 0) {
        formattedData.contact_numbers = data.contact_numbers
      }
      if (data.whatsapp) {
        formattedData.whatsapp = data.whatsapp;
      }
      if (data.email) {
        formattedData.email = data.email;
      }
      if (data.website) {
        formattedData.website = data.website;
      }

      const res = await http.put(`/dashboard/businesses/update-contact`, formattedData);
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update contact information"
      );
    }
  }
);

// Update shop discount information
export const updateShopDiscount = createAsyncThunk(
  "shops/updateShopDiscount",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      // Format data to match backend expectations
      const formattedData = {
        uuid: uuid,
      };

      // Add optional fields only if they exist
      if (data.discount_text) {
        formattedData.discount_text = data.discount_text;
      }
      if (data.fixed_discount !== undefined && data.fixed_discount !== null) {
        formattedData.fixed_discount = data.fixed_discount;
      }
      if (data.customer_discount !== undefined && data.customer_discount !== null) {
        formattedData.customer_discount = data.customer_discount;
      }
      if (data.company_commission !== undefined && data.company_commission !== null) {
        formattedData.company_commission = data.company_commission;
      }

      const res = await http.put(`/dashboard/businesses/update-discounts`, formattedData);
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update discount information"
      );
    }
  }
);

// Update shop gallery
export const updateShopGallery = createAsyncThunk(
  "shops/updateShopGallery",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      console.log('updateShopGallery called with:', { uuid, data });
      const formData = new FormData();

      // Add UUID to form data
      formData.append('uuid', uuid);

      // Add new gallery images
      if (data.gallery && data.gallery.length > 0) {
        data.gallery.forEach((file, index) => {
          // Handle both File objects directly and wrapped file objects
          if (file instanceof File) {
            formData.append(`gallery[${index}]`, file);
          } else if (file.file && !file.existing) {
            formData.append(`gallery[${index}]`, file.file);
          }
        });
      }

      // Add images to remove as individual array items
      if (data.remove_images && data.remove_images.length > 0) {
        data.remove_images.forEach((imageUuid, index) => {
          formData.append(`remove_images[${index}]`, imageUuid);
        });
      }

      const res = await http.post(`/dashboard/businesses/update-gallery`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update gallery"
      );
    }
  }
);

// Update business settings (subscription, payment modes, timings)
// Consolidated single API for update-business-settings endpoint
export const updateBusinessSettings = createAsyncThunk(
  "shops/updateBusinessSettings",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      // Format data to match backend validation rules
      const formattedData = {
        uuid: uuid,
      };

      // Add optional fields only if they exist
      if (data.subscription_package_id) {
        formattedData.subscription_package_id = data.subscription_package_id;
      }

      // Send payment_modes as array (backend expects: 'payment_modes' => 'sometimes|array')
      if (data.payment_modes && Array.isArray(data.payment_modes)) {
        formattedData.payment_modes = data.payment_modes;
      }

      // Send business_timings as nested array object (backend expects: 'business_timings' => 'sometimes|array')
      if (data.business_timings) {
        formattedData.business_timings = data.business_timings;
      }

      const res = await http.put(`/dashboard/businesses/update-business-settings`, formattedData);
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update business settings"
      );
    }
  }
);

// Update business location
export const updateBusinessLocation = createAsyncThunk(
  "shops/updateBusinessLocation",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      // Format data to match backend expectations
      const formattedData = {
        uuid: uuid,
      };

      // Add optional fields only if they exist
      if (data.state_id) {
        formattedData.state_id = data.state_id;
      }
      if (data.district_id) {
        formattedData.district_id = data.district_id;
      }
      if (data.taluka_id) {
        formattedData.taluka_id = data.taluka_id;
      }
      if (data.address) {
        formattedData.address = data.address;
      }
      if (data.pincode) {
        formattedData.pincode = data.pincode;
      }
      if (data.business_location_map) {
        formattedData.business_location_map = data.business_location_map;
      }
      if (data.latitude !== null && data.latitude !== undefined) {
        formattedData.latitude = data.latitude;
      }
      if (data.longitude !== null && data.longitude !== undefined) {
        formattedData.longitude = data.longitude;
      }

      const res = await http.put(`/dashboard/businesses/update-location`, formattedData);
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update business location"
      );
    }
  }
);

// Update business categories and services
export const updateBusinessCategoriesServices = createAsyncThunk(
  "shops/updateBusinessCategoriesServices",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      // Format data to match backend expectations
      const formattedData = {
        uuid: uuid,
      };

      // Add optional fields only if they exist
      if (data.category_id) {
        formattedData.category_id = data.category_id;
      }
      if (data.sub_categories && Array.isArray(data.sub_categories)) {
        formattedData.sub_categories = data.sub_categories;
      }
      if (data.services && Array.isArray(data.services)) {
        formattedData.services = data.services;
      }

      const res = await http.put(`/dashboard/businesses/update-categories-services`, formattedData);
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update categories and services"
      );
    }
  }
);

// Update business info
export const updateBusinessInfo = createAsyncThunk(
  "shops/updateBusinessInfo",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      // Format data to match backend expectations
      const formattedData = {
        uuid: uuid,
      };

      // Add optional fields only if they exist
      if (data.business_name) {
        formattedData.business_name = data.business_name;
      }
      if (data.about_business !== undefined) {
        formattedData.about_business = data.about_business;
      }
      if (data.username) {
        formattedData.username = data.username;
      }
      if (data.is_trending !== undefined) {
        formattedData.is_trending = data.is_trending ? true : false;
      }
      if (data.is_recently_added !== undefined) {
        formattedData.is_recently_added = data.is_recently_added ? true : false;
      }

      const res = await http.put(`/dashboard/businesses/update-basic-info`, formattedData);
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update business information"
      );
    }
  }
);

// Create user account for business
export const createUserAccount = createAsyncThunk(
  "shops/createUserAccount",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.post(`/dashboard/businesses/create-user-account`, { uuid });
      return { uuid, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create user account"
      );
    }
  }
);

const shopsSlice = createSlice({
  name: "shop",
  initialState: {
    shops: [],
    pagination: null,
    shop: null,
    shopCreateStatus: "loading",
    shopCreateError: null,
    shopChangeStatus: "loading",
    shopFetchStatus: "idle",
    shopUpdateStatus: "idle",
    shopUpdateError: null,
    shopContactUpdateStatus: "idle",
    shopDiscountUpdateStatus: "idle",
    shopGalleryUpdateStatus: "idle",
    businessInfoUpdateStatus: "idle",
    businessSettingsUpdateStatus: "idle",
    businessLocationUpdateStatus: "idle",
    businessCategoriesServicesUpdateStatus: "idle",
    createUserAccountStatus: "idle",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchShops.fulfilled, (state, action) => {
        state.shops = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(updateShopStatus.pending, (state) => {
        state.shopChangeStatus = "loading";
      })
      .addCase(updateShopStatus.rejected, (state) => {
        state.shopChangeStatus = "failed";
      })
      .addCase(updateShopStatus.fulfilled, (state, action) => {
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.shops[index].status =
            action.meta.arg.status === "enable" ? 1 : 0;
        }
        state.shopChangeStatus = "success";
      })

      .addCase(createShop.fulfilled, (state, action) => {
        state.shopCreateStatus = "success";
        state.shopCreateError = null;
        state.shop = null;
      })
      .addCase(createShop.rejected, (state, action) => {
        state.shop = null;
        state.shopCreateStatus = "failed";
        state.shopCreateError = action.payload;
      })

      .addCase(fetchShopById.pending, (state) => {
        state.shopFetchStatus = "loading";
      })
      .addCase(fetchShopById.fulfilled, (state, action) => {
        state.shopFetchStatus = "success";
        state.shop = action.payload.data;
      })
      .addCase(fetchShopById.rejected, (state) => {
        state.shopFetchStatus = "failed";
        state.shop = null;
      })

      .addCase(updateShop.pending, (state) => {
        state.shopUpdateStatus = "loading";
        state.shopUpdateError = null;
      })
      .addCase(updateShop.fulfilled, (state, action) => {
        state.shopUpdateStatus = "success";
        state.shopUpdateError = null;
        // Update the shop in the shops array if it exists
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
        state.shop = action.payload.data;
      })
      .addCase(updateShop.rejected, (state, action) => {
        state.shopUpdateStatus = "failed";
        state.shopUpdateError = action.payload;
      })

      // Update shop contact
      .addCase(updateShopContact.pending, (state) => {
        state.shopContactUpdateStatus = "loading";
      })
      .addCase(updateShopContact.fulfilled, (state, action) => {
        state.shopContactUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateShopContact.rejected, (state) => {
        state.shopContactUpdateStatus = "failed";
      })

      // Update shop discount
      .addCase(updateShopDiscount.pending, (state) => {
        state.shopDiscountUpdateStatus = "loading";
      })
      .addCase(updateShopDiscount.fulfilled, (state, action) => {
        state.shopDiscountUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateShopDiscount.rejected, (state) => {
        state.shopDiscountUpdateStatus = "failed";
      })

      // Update shop gallery
      .addCase(updateShopGallery.pending, (state) => {
        state.shopGalleryUpdateStatus = "loading";
      })
      .addCase(updateShopGallery.fulfilled, (state, action) => {
        state.shopGalleryUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateShopGallery.rejected, (state) => {
        state.shopGalleryUpdateStatus = "failed";
      })

      // Update business info
      .addCase(updateBusinessInfo.pending, (state) => {
        state.businessInfoUpdateStatus = "loading";
      })
      .addCase(updateBusinessInfo.fulfilled, (state, action) => {
        state.businessInfoUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateBusinessInfo.rejected, (state) => {
        state.businessInfoUpdateStatus = "failed";
      })

      // Update business settings
      .addCase(updateBusinessSettings.pending, (state) => {
        state.businessSettingsUpdateStatus = "loading";
      })
      .addCase(updateBusinessSettings.fulfilled, (state, action) => {
        state.businessSettingsUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateBusinessSettings.rejected, (state) => {
        state.businessSettingsUpdateStatus = "failed";
      })

      // Update business location
      .addCase(updateBusinessLocation.pending, (state) => {
        state.businessLocationUpdateStatus = "loading";
      })
      .addCase(updateBusinessLocation.fulfilled, (state, action) => {
        state.businessLocationUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateBusinessLocation.rejected, (state) => {
        state.businessLocationUpdateStatus = "failed";
      })

      // Update business categories and services
      .addCase(updateBusinessCategoriesServices.pending, (state) => {
        state.businessCategoriesServicesUpdateStatus = "loading";
      })
      .addCase(updateBusinessCategoriesServices.fulfilled, (state, action) => {
        state.businessCategoriesServicesUpdateStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(updateBusinessCategoriesServices.rejected, (state) => {
        state.businessCategoriesServicesUpdateStatus = "failed";
      })

      // Create user account
      .addCase(createUserAccount.pending, (state) => {
        state.createUserAccountStatus = "loading";
      })
      .addCase(createUserAccount.fulfilled, (state, action) => {
        state.createUserAccountStatus = "success";
        const index = state.shops.findIndex(
          (shop) => shop.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.shops[index] = { ...state.shops[index], ...action.payload.data };
        }
      })
      .addCase(createUserAccount.rejected, (state) => {
        state.createUserAccountStatus = "failed";
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })

      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default shopsSlice.reducer;
